'use client'

import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import ApplyForm from './ApplyForm'

const OPEN_EVENT = 'ncp:apply'

/** Opens the application modal, optionally with a course slug preselected */
export function openApply(course?: string) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { course } }))
}

const TEXT = {
  en: { title: 'Apply to Nippon Center Pakistan', sub: 'Fill in a few details and our admissions team will get back to you with batch timings and fees.', close: 'Close' },
  ja: { title: 'Nippon Center Pakistan へのお申し込み', sub: '必要事項をご記入ください。担当者よりクラスの時間と費用をご案内します。', close: '閉じる' },
}

/** Application modal, mounted once in the root layout and opened by <ApplyButton> */
export function ApplyDialog() {
  const pathname = usePathname() ?? ''
  const locale = pathname.startsWith('/ja') ? 'ja' : 'en'
  const t = TEXT[locale]
  const [open, setOpen] = useState(false)
  const [course, setCourse] = useState('')
  // Bumped on every open so the form starts fresh each time
  const [session, setSession] = useState(0)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    const onOpen = (e: Event) => {
      setCourse((e as CustomEvent<{ course?: string }>).detail?.course ?? '')
      setSession((s) => s + 1)
      setOpen(true)
    }
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, close])

  // Close when navigating to another page
  useEffect(() => close(), [pathname, close])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="apply-dialog-title"
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4"
    >
      <div className="absolute inset-0 bg-brand-navy/60 backdrop-blur-sm" onClick={close} aria-hidden="true" />

      <div className="relative z-10 flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        <div className="flex items-start justify-between gap-4 border-b border-brand-cool-gray px-6 py-5 sm:px-8">
          <div className="space-y-1">
            <h2 id="apply-dialog-title" className="text-xl font-semibold tracking-tight text-brand-navy">
              {t.title}
            </h2>
            <p className="text-sm text-brand-muted">{t.sub}</p>
          </div>
          <button
            type="button"
            onClick={close}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-brand-muted transition-colors hover:bg-brand-soft-gray hover:text-brand-charcoal"
            aria-label={t.close}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-6 sm:px-8">
          <ApplyForm key={session} locale={locale} initialCourse={course} />
        </div>
      </div>
    </div>
  )
}
